import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DeviceService} from '../../commons/device.service';
import {MonitorMarkerService} from '../../map/monitor-marker.service';
import {ZOOM} from '../map-address-search/map-address-search.component';
import {TaskUtils} from '../../../pages/client/tasks/tasks-manager/utils/task-utils';
import {
  InfoWindowContent, ProTasksMapMarkerService
} from '../../../pages/client/tasks/pro-tasks-manager/marker-service/pro-tasks-map-marker.service';
import {FuelingPoiEntry} from './model/fueling-poi.entry';
import {AuthStorage} from '../../auth/auth-storage';
import {TrailerSwapMarkerService} from '../../../pages/client/tasks/trailer-swap/trailer-swap-marker.service';
import {Module} from '../../model/profile';
import {escapeForOnclickArg} from "../../map/map-popup-string.utils";
import {
  FuelStationInformation
} from '../../../pages/client/tasks/pro-tasks-manager/form/model/fuel-station-information';
import {MapPosition} from '../../../pages/client/tasks/route/metamodel';
import {
  formatFuelPrice
} from '../../../pages/client/tasks/pro-tasks-manager/form/properties/fuel-chart/fuel-calculation.utils';

export const DIESEL_COLOR = '#000000'
export const ADBLUE_COLOR = '#3153bf'
export const LNG_COLOR = '#975a5a'
export const HVO_COLOR = '#2f855a'
export const CNG_COLOR = '#38a169'
export const BIODIESEL_COLOR = '#6b8e23'
export const BEV_COLOR = '#3182ce'
export const HYDROGEN_COLOR = '#63b3ed'

@Injectable({
  providedIn: 'root'
})
export class FuelingPoiMarkerService {

  public static readonly TRACK_NG_ZONE = 'onclick="window.angularTrackRef.zone.run(() => '
    + '{window.angularTrackRef.component.';

  constructor(private translate: TranslateService,
              private deviceService: DeviceService) {
  }

  public getGoogleMapsContent(poi: FuelingPoiEntry, taskCount: number): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 80 : 70,
      bubbleWidth: 300
    };
    info.content += `<div class="popup-content"><div class="popup-header"><span>${poi.name}</span></div>`;
    info.content += '<div class="ol-info-data">';
    this.showStationPrices(poi, info);
    this.addPoiGroupInfo(poi, info, this.translate);
    info.content += '<div class="popup-buttons">';
    info.content += this.getMapAddressActionLink(`zoomMarker(${poi.latitude}, ${poi.longitude}, ${ZOOM.DELTA})`,
      'ico-zoom-in', 'zoomIn', this.translate);
    info.content += this.getMapAddressActionLink(`zoomMarker(${poi.latitude}, ${poi.longitude}, ${-ZOOM.DELTA})`,
      'ico-zoom-out', 'zoomOut', this.translate);

    const comment = poi.comment != null ? escapeForOnclickArg(poi.comment) : null;
    const contactlessType = TaskUtils.resolveContactless(poi);
    for (let i = 0; i <= taskCount; i++) {
      let insertTask = '<a class="icon-action" '
        + `title="${this.translate.instant('monitor.map.title.insertInto')}"`
        + `onclick="window.angularPoiRef.zone.run(() => `
        + `{window.angularPoiRef.component.insertInto(${poi.latitude}, ${poi.longitude}, `
        + `${i}, '${poi.externalId}', '${poi.poiId}', '${contactlessType}', '${comment}')})">`
        + `${i}  `
        + '<i class="ico-arrow-right"></i>'
        + `  ${i + 1}`
        + '</a>';
      info.content += `${insertTask}`;
    }

    info.content += '</div></div>';
    return info;
  }

  public getOLContent(poi: FuelingPoiEntry, uuid: string, type: string): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 80 : 70,
      bubbleWidth: 300
    };
    info.content += '<div class="popup-content"><div class="ol-popup-header"><span>' + poi.name + '</span></div>';
    info.content += '<div class="ol-info-data">';
    this.showStationPrices(poi, info);
    this.addPoiGroupInfo(poi, info, this.translate);
    info.content += '<div class="popup-buttons">';
    info.content += this.getActionLink(`addToRoute('${uuid}', '${type}')`, 'ico-location-arrow', 'route', this.translate);
    info.content += '</div></div>';
    return info;
  }

  public getTaskOLContent(
    poi: FuelingPoiEntry,
    uuid: string,
    zone: string): InfoWindowContent {

    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    let activeNgZone = this.resolveActiveZone(zone);
    let header = `<div class="ol-popup-header"><span class="map-popup__title">${poi.name}</span></div>`;
    info.content += '<div class="popup-content">' + header;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.coordinates')
      + `</span><a target="_blank" href="http://www.google.com/maps/place/${poi.latitude},${poi.longitude}">`
      + `: ${poi.latitude}, ${poi.longitude}</a>`
      + `<a class="map-popup__link" target="_blank" href="https://www.google.com/maps?q=&layer=c&cbll=${poi.latitude},${poi.longitude}">`
      + '<i class="ico-human"></i></a>'
      + '<a class="map-popup__link" '
      + 'title ="' + this.translate.instant('monitor.map.title.' + 'copy') + '"'
      + activeNgZone + `copyToClipboard('${poi.latitude},${poi.longitude}')` + '})">'
      + '<i class="ico-copy"></i></a>'
      + '</div></div>';
    info.bubbleHeight += 13;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.address')
      + `</span>: ${poi.address}</div>`;
    if (poi.address?.length > 30) {
      info.bubbleHeight += 13;
    }
    this.showStationPrices(poi, info);
    this.createContactlessInfo(info, poi);
    this.createInfoLine(info, this.translate.instant('monitor.poi.group'), poi.groupName, '');
    this.createCommentLine(poi, info);
    info.content += '<div class="popup-buttons">';

    if (this.canManageTasks() && zone === 'queue') {
      info.content += '<a class="icon-action" ' +
        'title ="' + this.translate.instant('monitor.map.title.route') + '"' +
        activeNgZone + `addToRoute('${uuid}')` + '})"><i class="ico-location-arrow"></i></a>'
    }

    if (this.canManageTasks()
      && !AuthStorage.isForbiddenManageTasks()
      && (zone === 'form' || zone === 'queue' || zone === 'task-forms')) {

      info.content += this.taskInsertActionLink(poi, 'autoImport', false, activeNgZone, false);
      info.content += this.taskInsertActionLink(poi, 'importToLastPosition', true, activeNgZone, false);
      if (zone === 'form' || zone === 'task-forms') {
        info.content += this.taskInsertActionLink(poi, 'importSelected', false, activeNgZone, true);
      }
    }
    info.content += '</div></div>';
    return info;
  }

  public getSuggestedFuelTaskOLContent(
    destination: string,
    destinationPosition: MapPosition,
    comments: string,
    fuelStationInformation: FuelStationInformation): InfoWindowContent {

    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    let title = destination || fuelStationInformation?.groupName || '';
    let header = `<div class="ol-popup-header"><span class="map-popup__title">${title}</span></div>`;
    info.content += '<div class="popup-content">' + header;
    this.showFuelStationInformationPrices(fuelStationInformation, info);
    if (destinationPosition != null) {
      let lat = destinationPosition.lat;
      let lng = destinationPosition.lng;
      info.content += '<div class="ol-info-data">';
      info.content += '<div class="info-line"><span class="info-label">'
        + this.translate.instant('vehicles.dvr.track.infoWindow.location')
        + `</span><a target="_blank" href="http://www.google.com/maps/place/${lat},${lng}">`
        + `: ${lat}, ${lng}</a>`
        + `<a class="map-popup__link" target="_blank" href="https://www.google.com/maps?q=&layer=c&cbll=${lat},${lng}">`
        + '<i class="ico-human"></i></a>'
        + '</div>';
      info.bubbleHeight += 13;
    }
    if (fuelStationInformation?.groupName) {
      this.createInfoLine(info, this.translate.instant('monitor.poi.group'), fuelStationInformation.groupName, '');
    }
    if (comments) {
      let commentLines = MonitorMarkerService.splitToLines(comments, info);
      this.createInfoLine(info, this.translate.instant('monitor.poi.comment'), commentLines, '');
    }
    info.content += '</div></div>';
    return info;
  }

  private taskInsertActionLink(
    poi: FuelingPoiEntry,
    tooltip: string,
    autoInsert: boolean,
    activeNgZone: string,
    importSelected: boolean): string {

    const name = escapeForOnclickArg(poi.name);
    const comment = escapeForOnclickArg(poi.comment);
    const contactlessType = TaskUtils.resolveContactless(poi);
    return '<a class="icon-action"'
      + activeNgZone
      // eslint-disable-next-line max-len
      + `insertProTask(${poi.latitude}, ${poi.longitude}, ${autoInsert}, '${poi.externalId}', '${poi.poiId}', '${comment}', 'FUELLING', '${contactlessType}', '${null}', '${null}', '${name}', ${importSelected})})">`
      + `${this.translate.instant('proTasks.map.' + tooltip)}`
      + '</a>';
  }

  private canManageTasks(): boolean {
    return AuthStorage.isLoggedClientEmployee() || AuthStorage.getModules().includes(Module.MANAGEMENT);
  }

  private getActionLink(
    action: string,
    icon: string,
    tooltip: string,
    translate: TranslateService,
    customClass?: string): string {

    const linkStart = customClass
      ? `<a class="${customClass}"`
      : '<a class="icon-action" ';
    return linkStart
      + 'title ="' + translate.instant('monitor.map.title.' + tooltip) + '"'
      + 'onclick="window.angularMapRef.zone.run(() => '
      + '{window.angularMapRef.component.' + action + '})"><i class="' + icon + '"></i></a>';
  }

  private showStationPrices(poi: FuelingPoiEntry, info: InfoWindowContent): void {
    if (this.hasAnyFuelPrices(poi)) {
      info.content += '<div class="fuel-prices">';
      let currency = poi.currency != null ? poi.currency : '';
      if (poi.fuelPrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${DIESEL_COLOR}">DIESEL</span>`
          + `<span class="info-label">${formatFuelPrice(poi.fuelPrice)} ${currency}</span>`;
        info.bubbleHeight += 13;
      }
      if (poi.adbluePrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${ADBLUE_COLOR}">ADBLUE</span>`
          + `<span class="info-label">${formatFuelPrice(poi.adbluePrice)} ${currency}</span>`;
        info.bubbleHeight += 13;
      }
      if (poi.lngPrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${LNG_COLOR}">LNG</span>`
          + `<span class="info-label">${formatFuelPrice(poi.lngPrice)} ${currency}</span>`;
        info.bubbleHeight += 13;
      }
      if (poi.hvoPrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${HVO_COLOR}">HVO</span>`
          + `<span class="info-label">${formatFuelPrice(poi.hvoPrice)} ${currency}</span>`;
        info.bubbleHeight += 13;
      }
      if (poi.cngPrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${CNG_COLOR}">CNG</span>`
          + `<span class="info-label">${formatFuelPrice(poi.cngPrice)} ${currency}</span>`;
        info.bubbleHeight += 13;
      }
      if (poi.biodieselPrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${BIODIESEL_COLOR}">BIODIESEL</span>`
          + `<span class="info-label">${formatFuelPrice(poi.biodieselPrice)} ${currency}</span>`;
        info.bubbleHeight += 13;
      }
      if (poi.bevPrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${BEV_COLOR}">BEV</span>`
          + `<span class="info-label">${formatFuelPrice(poi.bevPrice)} ${currency}</span>`;
        info.bubbleHeight += 13;
      }
      if (poi.hydrogenPrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${HYDROGEN_COLOR}">H₂</span>`
          + `<span class="info-label">${formatFuelPrice(poi.hydrogenPrice)} ${currency}</span>`;
        info.bubbleHeight += 13;
      }
      info.content += '</div>';
    }
  }

  private showFuelStationInformationPrices(station: FuelStationInformation, info: InfoWindowContent): void {
    if (station?.fuelPrice != null || station?.adbluePrice != null || station?.lngPrice != null) {
      info.content += '<div class="fuel-prices">';
      if (station.fuelPrice != null && station.fuelPrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${DIESEL_COLOR}">DIESEL</span>`
          + '<span class="info-label">' + formatFuelPrice(station.fuelPrice) + ' '
          + this.translate.instant('global.currency.EUR') + '</span>';
        info.bubbleHeight += 13;
      }
      if (station.adbluePrice != null && station.adbluePrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${ADBLUE_COLOR}">ADBLUE</span>`
          + '<span class="info-label">' + formatFuelPrice(station.adbluePrice) + ' '
          + this.translate.instant('global.currency.EUR') + '</span>';
        info.bubbleHeight += 13;
      }
      if (station.lngPrice != null && station.lngPrice > 0) {
        info.content += `<span class="fuel-type" style="background-color: ${LNG_COLOR}">LNG</span>`
          + '<span class="info-label">' + formatFuelPrice(station.lngPrice) + ' '
          + this.translate.instant('global.currency.EUR') + '</span>';
        info.bubbleHeight += 13;
      }
      info.content += '</div>';
    }
  }

  private addPoiGroupInfo(poi: FuelingPoiEntry, info: InfoWindowContent, translate: TranslateService) {
    this.createInfoLine(
      info,
      translate.instant('monitor.poi.address'),
      MonitorMarkerService.splitToLines(poi.address, info),
      '');
    if (poi.address && poi.address.length > 30) {
      info.bubbleHeight += 13;
    }
    info.content += '<div class="info-line"><span class="info-label">'
      + translate.instant('vehicles.dvr.track.infoWindow.location')
      + '</span>: <a target="_blank" href="http://www.google.com/maps/place/'
      + poi.latitude + ',' + poi.longitude + '">' + poi.latitude
      + ', ' + poi.longitude + '</a>       '
      + '<a class="map-popup__link" target="_blank" href="https://www.google.com/maps?q=&layer=c&cbll='
      + poi.latitude + ',' + poi.longitude + '">'
      + '<i class="ico-human"></i></a></div>';
    info.bubbleHeight += 13;
    this.createContactlessInfo(info, poi);
    this.createInfoLine(info, translate.instant('monitor.poi.group'), poi.groupName, '');
    this.createCommentLine(poi, info);
  }

  private createCommentLine(poi: FuelingPoiEntry, info: InfoWindowContent): void {
    if (poi.comment) {
      const mergedComment = [poi.name, poi.comment].filter(v => v).join(' - ');
      let commentLines = MonitorMarkerService.splitToLines(mergedComment, info);
      this.createInfoLine(info, this.translate.instant('monitor.poi.comment'), commentLines, '');
    }
  }

  private createInfoLine(info: InfoWindowContent, name: string, value: any, units: string): void {
    info.content += '<div class="info-line"><span class="info-label">'
      + name + '</span>: ' + value + ' ' + units + '</div>';
    info.bubbleHeight += 13;
  }

  private getMapAddressActionLink(action: string, icon: string, tooltip: string, translate: TranslateService): string {
    return '<a class="icon-action" '
      + 'title ="' + translate.instant('monitor.map.title.' + tooltip) + '" '
      + 'onclick="window.angularFuelingPoiRef.zone.run(() => '
      + '{window.angularFuelingPoiRef.component.' + action + '})"><i class="' + icon + '"></i></a>';
  }

  private createContactlessInfo(info: InfoWindowContent, entry: FuelingPoiEntry): void {
    if (entry.contactless) {
      info.content += '<div class="info-line"><span class="info-label">'
        + this.translate.instant('fueling.contactless')
        + `</span>: <i class="ico-check c-green"></i></div>`;
      info.bubbleHeight += 13;
    }
    if (entry.contactlessOnly) {
      info.content += '<div class="info-line"><span class="info-label">'
        + this.translate.instant('fueling.contactlessOnly')
        + `</span>: <i class="ico-check c-green"></i></div>`;
      info.bubbleHeight += 13;
    }
  }

  private resolveActiveZone(zone: string): string {
    switch (zone) {
      case 'task-forms':
        return TrailerSwapMarkerService.trailerSwapFormZone;
      case 'vehicle-select':
        return TrailerSwapMarkerService.trailerSwapSelectZone;
      case 'form':
        return ProTasksMapMarkerService.FORM_NG_ZONE;
      case 'queue':
        return ProTasksMapMarkerService.QUEUE_NG_ZONE;
      case 'done':
        return ProTasksMapMarkerService.DONE_NG_ZONE;
      case 'track':
        return FuelingPoiMarkerService.TRACK_NG_ZONE;
      default:
        return ProTasksMapMarkerService.QUEUE_NG_ZONE;
    }
  }

  private hasAnyFuelPrices(poi: FuelingPoiEntry): boolean {
    return [
      poi?.fuelPrice,
      poi?.adbluePrice,
      poi?.lngPrice,
      poi?.hvoPrice,
      poi?.cngPrice,
      poi?.biodieselPrice,
      poi?.bevPrice,
      poi?.hydrogenPrice
    ].some(price => price != null);
  }
}
